import * as React from "react"
import {
  useColorMode,
  useColorModeValue,
  IconButton,
  IconButtonProps,
  Avatar,
  Box,
  CloseButton,
  Flex,
  HStack,
  VStack,
  Icon,
  Text,
  Drawer,
  DrawerContent,
  useDisclosure,
  BoxProps,
  FlexProps,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
} from '@chakra-ui/react'
import {
  FiMenu,
  FiBell,
  FiChevronDown,
} from 'react-icons/fi'
import { IconType } from 'react-icons'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { getNameFromToken, getRoleFromToken } from '../../util/IsAuth';

import { ColorModeSwitcher } from '../ColorModeSwitcher'; 

interface NavItemProps extends FlexProps {
  icon: IconType
  href: string
  children: React.ReactNode
}

interface MobileProps extends FlexProps {
  onOpen: () => void
}

interface SidebarProps extends BoxProps {
  onClose: () => void
}

interface ISidebarWithHeaderProps {
  navContent: React.ReactNode;
  children: React.ReactNode;
}

const SidebarContent = ({ onClose, children, ...rest }: SidebarProps & { children: React.ReactNode }) => {
  return (
    <Box
      transition="3s ease"
      bg={useColorModeValue('white', 'gray.900')}
      borderRight="1px"
      borderRightColor={useColorModeValue('gray.200', 'gray.700')}
      w={{ base: 'full', md: 60 }}
      pos="fixed" // Keep sidebar fixed
      h="full"
      {...rest}>
      <Flex h="20" alignItems="center" mx="8" justifyContent="space-between">
        <Box
          h={{ base: '150px', md: '200px' }}
          w={'full'}
          bgImage={"url('assets/images/jpg/png/two_fish_logo.jpg')"}
          bgSize={'30%'}
          bgPos={'center'}
          bgRepeat={'no-repeat'}
        />
        <CloseButton display={{ base: 'flex', md: 'none' }} onClick={onClose} />
      </Flex>
      {/* Renders the navigation items in a flex container */}
      <VStack spacing="0" align="stretch">
        {children}
      </VStack>
    </Box>
  )
}

export const NavItem = ({ icon, href, children, ...rest }: NavItemProps) => {
  return (
    <Box
      as={RouterLink}
      to={href}
      style={{ textDecoration: 'none' }}
      _focus={{ boxShadow: 'none' }}>
      <Flex
        align="center"
        p="4"
        mx="4"
        borderRadius="lg"
        role="group"
        cursor="pointer"
        _hover={{
          bg: 'cyan.400',
          color: 'white',
        }}
        {...rest}>
        {icon && (
          <Icon
            mr="4"
            fontSize="16"
            _groupHover={{
              color: 'white',
            }}
            as={icon}
          />
        )}
        {children}
      </Flex>
    </Box>
  )
}

const MobileNav = ({ onOpen, ...rest }: MobileProps) => {

  const navigate = useNavigate(); // Initialize useNavigate hook

  const handleSignOut = () => {
    localStorage.removeItem('token'); // Clear the authentication token
    // You might also want to clear other user-related data from localStorage/sessionStorage
    navigate('/login'); // Redirect to the login page
  };

  return (
    <Flex
      ml={{ base: 0, md: 60 }}
      px={{ base: 4, md: 4 }}
      height="20"
      alignItems="center"
      bg={useColorModeValue('white', 'gray.900')}
      borderBottomWidth="1px"
      borderBottomColor={useColorModeValue('gray.200', 'gray.700')}
      justifyContent={{ base: 'space-between', md: 'flex-end' }}
      position="fixed" 
      top="0" 
      width={{ base: 'full', md: 'calc(100% - 240px)' }}
      zIndex="sticky"
      {...rest}>
      <IconButton
        display={{ base: 'flex', md: 'none' }}
        onClick={onOpen}
        variant="outline"
        aria-label="open menu"
        icon={<FiMenu />}
      />

      {/* <Text
        display={{ base: 'flex', md: 'none' }}
        fontSize="2xl"
        fontFamily="monospace"
        fontWeight="bold">
        Aquatics Automation
      </Text> */}

      <HStack spacing={{ base: '0', md: '6' }}>
        <ColorModeSwitcher />
        {/* <IconButton size="lg" variant="ghost" aria-label="open menu" icon={<FiBell />} /> */}
        <Flex alignItems={'center'}>
          <Menu>
            <MenuButton py={2} transition="all 0.3s" _focus={{ boxShadow: 'none' }}>
              <HStack>
                <Avatar
                  size={'sm'}
                  src={
                    "url()" // Placeholder for avatar image
                  }
                />
                <VStack
                  display={{ base: 'none', md: 'flex' }}
                  alignItems="flex-start"
                  spacing="1px"
                  ml="2">
                  <Text fontSize="sm">
                    {getNameFromToken()}
                    </Text>
                  <Text fontSize="xs" color="gray.600">
                    {getRoleFromToken()}
                  </Text>
                </VStack>
                <Box display={{ base: 'none', md: 'flex' }}>
                  <FiChevronDown />
                </Box>
              </HStack>
            </MenuButton>
            <MenuList
              bg={useColorModeValue('white', 'gray.900')}
              borderColor={useColorModeValue('gray.200', 'gray.700')}>
              <MenuItem>Profile</MenuItem>
              <MenuItem>Settings</MenuItem>
              <MenuDivider />
              <MenuItem onClick={handleSignOut}>Sign out</MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </HStack>
    </Flex>
  )
}

export const SidebarWithHeader = ({ navContent, children }: ISidebarWithHeaderProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure()

  return (
    <Box minH="100vh" bg={useColorModeValue('gray.100', 'gray.900')}>
      <SidebarContent onClose={onClose} display={{ base: 'none', md: 'block' }}>
        {navContent}
      </SidebarContent>
      <Drawer
        isOpen={isOpen}
        placement="left"
        onClose={onClose}
        returnFocusOnClose={false}
        onOverlayClick={onClose}
        size="full"
      >
        <DrawerContent>
          <SidebarContent onClose={onClose}>{navContent}</SidebarContent>
        </DrawerContent>
      </Drawer>
      <MobileNav onOpen={onOpen} />
      <Box 
        ml={{ base: 0, md: 60 }} 
        p="4" 
        pt="20"
        height="100vh" // Set the container to the full viewport height
        overflowY="auto" // This makes the content box scrollable
      >
        {children}
      </Box>
    </Box>
  )
}